import { z } from 'zod';
import { CliError, CliErrorCode } from '@wix/cli-error';
import {
  readJson,
  pathExists,
  readPackageJson,
  writePackageJson,
} from '@wix/cli-fs';
import type { DependenciesUpdaterOptions } from './types.js';

const dependenciesJsonSchema = z.object({
  dependencies: z.record(z.string()).optional(),
  devDependencies: z.record(z.string()).optional(),
});

async function readDependenciesJson(filePath: string) {
  const json = await readJson(filePath);
  const parsedJson = await dependenciesJsonSchema.safeParseAsync(json);

  if (!parsedJson.success) {
    throw new CliError({
      code: CliErrorCode.InvalidDependenciesJsonSchemaError({
        path: filePath,
        zodError: parsedJson.error,
      }),
      cause: parsedJson.error,
    });
  }

  return parsedJson.data;
}

export async function dependenciesUpdater(opts: DependenciesUpdaterOptions) {
  const { projectFolder, dependenciesJsonPath, dynamicPackages } = opts;
  const addedDependencies: string[] = [];

  if (!(await pathExists(dependenciesJsonPath))) {
    return addedDependencies;
  }

  const dependenciesConfig = await readDependenciesJson(dependenciesJsonPath);

  const packageJson = await readPackageJson(projectFolder);
  const packageJsonDependencies = {
    ...packageJson.dependencies,
    ...packageJson.devDependencies,
  };

  if (dependenciesConfig.dependencies) {
    for (const [dependencyPackageName, version] of Object.entries(
      dependenciesConfig.dependencies
    )) {
      if (!packageJsonDependencies[dependencyPackageName]) {
        if (!packageJson.dependencies) {
          packageJson.dependencies = {};
        }

        packageJson.dependencies[dependencyPackageName] = version;
        addedDependencies.push(dependencyPackageName);
      }
    }
  }

  if (dynamicPackages) {
    for (const [dependencyPackageName, version] of Object.entries(
      dynamicPackages
    )) {
      if (!packageJsonDependencies[dependencyPackageName]) {
        if (!packageJson.dependencies) {
          packageJson.dependencies = {};
        }

        packageJson.dependencies[dependencyPackageName] = version;
        addedDependencies.push(dependencyPackageName);
      }
    }
  }

  if (dependenciesConfig.devDependencies) {
    for (const [dependencyPackageName, version] of Object.entries(
      dependenciesConfig.devDependencies
    )) {
      if (!packageJsonDependencies[dependencyPackageName]) {
        if (!packageJson.devDependencies) {
          packageJson.devDependencies = {};
        }

        packageJson.devDependencies[dependencyPackageName] = version;
        addedDependencies.push(dependencyPackageName);
      }
    }
  }

  if (addedDependencies.length) {
    await writePackageJson(projectFolder, packageJson);
  }

  return addedDependencies;
}
